import moment from "moment";
import React from "react";
import ReactMarkdown from "react-markdown";
import { Link } from "react-router-dom";
import "./ChatBot.css";
import { ChatMessage as ChatMessageType } from "./ChatBot.types";

interface ChatMessageProps {
  readonly message: ChatMessageType;
}

// Markdown component overrides
const MarkdownLink = ({
  children,
  href,
  ...props
}: React.ComponentPropsWithoutRef<"a">) => {
  // Check if it's a fully qualified URL
  const isFullUrl =
    href && (href.startsWith("http://") || href.startsWith("https://"));

  if (isFullUrl) {
    return (
      <a
        {...props}
        href={href}
        rel="noopener noreferrer"
        className="external-link"
        style={{
          color: "#007bff",
          textDecoration: "underline",
          fontWeight: "500",
        }}
      >
        {children}
      </a>
    );
  }

  // Check if it's a chatbot report link (/chatbot/:reportId)
  // If so, add sessionId from current URL if it exists
  let finalHref = href;
  let isInternalLink = false;
  if (href && href.startsWith("/chatbot/") && !href.includes("/sessionId/")) {
    const currentPath = window.location.pathname;
    const sessionIdMatch = currentPath.match(/\/chatbot\/sessionId\/([^/]+)/);
    if (sessionIdMatch) {
      const sessionId = sessionIdMatch[1];
      const reportIdMatch = href.match(/\/chatbot\/(.+)/);
      if (reportIdMatch) {
        const reportId = reportIdMatch[1];
        finalHref = `/chatbot/sessionId/${sessionId}/report/${reportId}`;
        isInternalLink = true;
      }
    }
  } else if (href && href.startsWith("/") && !href.startsWith("//")) {
    // Internal link (starts with / but not //)
    isInternalLink = true;
  }

  if (isInternalLink) {
    return (
      <Link
        {...props}
        to={finalHref || href || "#"}
        style={{
          color: "#007bff",
          textDecoration: "underline",
          fontWeight: "500",
        }}
      >
        {children}
      </Link>
    );
  }

  return (
    <a {...props} href={finalHref} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  );
};

const MarkdownCode = ({
  className,
  ...props
}: React.ComponentPropsWithoutRef<"code">) => {
  const isInline = !className;
  return isInline ? (
    <code className="inline-code" {...props} />
  ) : (
    <pre className="code-block">
      <code className={className} {...props} />
    </pre>
  );
};

// Function to preprocess content and convert URLs to markdown links
// Only converts standalone URLs, not URLs already in markdown links
const preprocessContent = (content: string): string => {
  // Unescape escaped markdown links: \[text\](url) or \[text](url) → [text](url)
  // The LLM occasionally escapes square brackets, causing ReactMarkdown to render
  // the link as literal text instead of a clickable anchor.
  let result = content.replace(
    /\\\[([^\]]*)\\\]\((https?:\/\/[^\s)]+)\)/g,
    "[$1]($2)",
  );
  result = result.replace(
    /\\\[([^\]]*)\]\((https?:\/\/[^\s)]+)\)/g,
    "[$1]($2)",
  );

  // Remove spaces inside markdown link URLs — a trailing space in the HOST env var on the
  // backend produces URLs like "https://domain.com /path" which breaks CommonMark link parsing.
  result = result.replace(
    /\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g,
    (_match, text, url) => `[${text}](${url.replace(/\s+/g, "")})`,
  );

  // Match URLs that are NOT already inside markdown link syntax [text](url)
  const urlRegex = /(?<!\]\()https?:\/\/[^\s)]+(?!\))/g;
  return result.replace(urlRegex, "[$&]($&)");
};

const markdownComponents = {
  a: MarkdownLink,
  code: MarkdownCode,
};

export function ChatMessage({ message }: ChatMessageProps) {
  const isUser = message.role === "user";
  const isAssistant = message.role === "assistant";

  return (
    <div className={`chat-message-wrapper ${isUser ? "user" : "assistant"}`}>
      <div
        className={`chat-message ${
          isUser ? "user-message" : "assistant-message"
        }`}
      >
        <div className="message-content">
          {isAssistant ? (
            <ReactMarkdown components={markdownComponents}>
              {preprocessContent(message.content)}
            </ReactMarkdown>
          ) : (
            message.content
          )}
        </div>
        <div className="message-footer">
          <small className="text-muted">
            {moment(message.timestamp).format("HH:mm")}
          </small>
          {isAssistant &&
            message.toolsCalled &&
            message.toolsCalled.length > 0 && (
              <small className="text-muted ms-2">
                🔧 {message.toolsCalled.join(", ")}
              </small>
            )}
          {isAssistant && message.tokensUsed && (
            <small className="text-muted ms-2">
              📊 {message.tokensUsed} tokens
            </small>
          )}
        </div>
      </div>
    </div>
  );
}
